import React, { useState, useEffect, memo } from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import Collapse from '@material-ui/core/Collapse';
import { Subhead, Caption1, Body } from '../../../components/Typography';
import { PrimaryButton, ButtonRow } from '../../../components/Buttons';
import renderCurrentOpenStatus from '../utils/renderCurrentOpenStatus';
import ChildLocations from '../../ParentChildRefactor/components/ChildLocations';
import appConfig from '../../../../app-config';
import { getParentInformation } from '../../OrderSetting/orderSettingActions';
import WeeklyTimeSlots from '../../../layout/components/WeeklyTimeSlots';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  display: grid;
  grid-gap: 32px;
  margin-top: 24px;
  align-items: center;
  @media (max-width: 786px) {
    margin-top: 0;
    padding: 20px;
    border-radius: 16px;
    box-shadow: ${(props) =>
      props.isorderbuttonhidden ? '0-1px8px0rgba(0, 0, 0, 0.15)' : '0'};
    z-index: 999;
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const MobileDetails = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  grid-gap: 16px;
`;

const MobileBackChevron = styled.i`
  @media (min-width: 786px) {
    display: none;
  }
`;

const MobileLocationName = styled(Subhead)`
  grid-column: 2 / -1;
`;

const MobileAddress = styled.div`
  grid-column: 2;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr auto;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled(Body)`
  color: ${(props) => props.theme.darkCategoryTitleFontColor || '#737a87'};
  font-weight: 600;
  align-self: flex-start;
`;

const HoursWrapper = styled(Collapse)`
  grid-column: 2 / -1;
`;

const DesktopButtonRow = styled(ButtonRow)`
  @media (max-width: 786px) {
    display: none;
  }
`;

const MobileButtonRow = styled(ButtonRow)`
  @media (min-width: 786px) {
    display: none;
  }
`;

const LocationDetails = ({
  location,
  setLocationToShowOnMobile,
  orderFromHere,
  isOrderFromHereShowing,
  currentLocation,
  updateOrderSettingModalIsVisible,
  updateInterOrderType,
  isOrderButtonHidden,
  isChevronHidden,
  interOrderType,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [childLocations, setChildLocations] = useState(null);

  useEffect(() => {
    if (appConfig.isParentChildClient) {
      getParentInformation(location.id).then((business) => {
        setChildLocations(business.sub_businesses);
        return;
      });
    }
  }, [location.id]);

  const [
    idOfExpandedChildLocationPreview,
    setIdOfExpandedChildLocationPreview,
  ] = useState(null);

  const handleSetIdOfExpandedChildLocationPreview = (
    idOfExpandedChildLocationPreviewToSet,
  ) => {
    if (
      idOfExpandedChildLocationPreviewToSet === idOfExpandedChildLocationPreview
    ) {
      setIdOfExpandedChildLocationPreview(null);
    } else {
      setIdOfExpandedChildLocationPreview(
        idOfExpandedChildLocationPreviewToSet,
      );
    }
  };

  const handleHoursClick = (event) => {
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleOrderFromHere = (location) => {
    orderFromHere(location);
  };

  return (
    <Wrapper isOrderButtonHidden={isOrderButtonHidden}>
      {typeof window !== 'undefined' && window.innerWidth <= 768 && (
        <MobileDetails>
          {!isChevronHidden ? (
            <MobileBackChevron
              className='fal fa-chevron-left'
              onClick={() => setLocationToShowOnMobile(null)}
            />
          ) : null}
          <MobileLocationName id='LocationDetails-MobileLocationName'>
            {location.name}
          </MobileLocationName>
          <MobileAddress>
            {location?.line2 && (
              <Caption1 id='LocationDetails-Location'>{`${location.line2} - `}</Caption1>
            )}
            <Caption1>{location.line1}</Caption1>
            <Caption1>{renderCurrentOpenStatus(location)}</Caption1>
          </MobileAddress>
          {currentLocation && <Caption1>{location.distance}</Caption1>}
        </MobileDetails>
      )}
      <MobileButtonRow justifyContent='center'>
        {!isOrderButtonHidden ? (
          <PrimaryButton
            id='LocationDetails-MobileOrderButton'
            onClick={(e) => handleOrderFromHere(location)}
          >
            {location.accepting_orders && location.is_order_ahead_enabled
              ? t('orderSettingModal.orderFromHere')
              : t('orderSettingModal.seeMenu')}
          </PrimaryButton>
        ) : null}
      </MobileButtonRow>

      <Row onClick={handleHoursClick}>
        <Label id='LocationDetails-Hours'>{t('orderSettingModal.hours')}</Label>
        <Caption1>{renderCurrentOpenStatus(location)}</Caption1>
        <i className={`fal fa-chevron-${isExpanded ? 'down' : 'right'}`} />
        <HoursWrapper in={isExpanded}>
          <WeeklyTimeSlots
            id='LocationDetails-StoreHours'
            title='Store Hours'
            hours={location.hours}
          />
          <WeeklyTimeSlots
            id='LocationDetails-OrderingHours'
            title='Ordering Hours'
            hours={location.ordering_hours}
          />
        </HoursWrapper>
      </Row>
      <Row>
        <Label id='LocationDetails-PhoneLabel'>
          {t('orderSettingModal.phone')}
        </Label>
        <Caption1>{location.phone}</Caption1>
        <i className='fas fa-phone' />
      </Row>
      <Row>
        <Label id='LocationDetails-AddressLabel'>
          {t('orderSettingModal.address')}
        </Label>
        <div>
          <Caption1 id='LocationDetails-Location'>{location.line1}</Caption1>
          <Caption1>
            {location.city}, {location.state}
          </Caption1>
          <Caption1 id='LocationDetails-Zip'>{location.zip}</Caption1>
        </div>
        <i className='fas fa-map-marker-alt' />
      </Row>
      {location?.website_url && location.website_url.trim() !== '' && (
        <Row>
          <Label id='LocationDetails-AddressLabel'>
            {t('orderSettingModal.website')}
          </Label>
          <a
            href={
              location.website_url.startsWith('http')
                ? location.website_url
                : `https://${location.website_url}`
            }
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              textDecoration: 'none',
            }}
            target='_blank'
          >
            <Caption1
              id='LocationDetails-Location'
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                textDecoration: 'none',
              }}
            >
              {location.website_url
                .replace(/^https?:\/\//, '')
                .replace(/\/$/, '')}
            </Caption1>
          </a>
          <i className='fas fa-globe' />
        </Row>
      )}

      {appConfig.isParentChildClient && (
        <Row>
          <Label id='LocationDetails-AddressLabel'>
            {t('orderSettingModal.stores')}
          </Label>
          <div>
            {childLocations
              ? childLocations.map((subBusiness) => {
                  if (
                    subBusiness.accepted_order_types &&
                    subBusiness.accepted_order_types.includes(interOrderType)
                  )
                    return (
                      <ChildLocations
                        key={subBusiness.id}
                        subBusiness={subBusiness}
                        idOfExpandedChildLocationPreview={
                          idOfExpandedChildLocationPreview
                        }
                        setIdOfExpandedChildLocationPreview={
                          handleSetIdOfExpandedChildLocationPreview
                        }
                        handleOrderFromHere={handleOrderFromHere}
                      />
                    );
                })
              : null}
          </div>
        </Row>
      )}
      {!appConfig.isParentChildClient && (
        <DesktopButtonRow justifyContent='center'>
          {isOrderFromHereShowing && location.is_order_ahead_enabled && (
            <PrimaryButton
              id='LocationDetails-DesktopOrderButton'
              onClick={(e) => handleOrderFromHere(location)}
            >
              {t('orderSettingModal.orderFromHere')}
            </PrimaryButton>
          )}
        </DesktopButtonRow>
      )}
    </Wrapper>
  );
};

export default memo(LocationDetails);
